/**
 * Normalized lookup for data-contract DQX rules (quarantine `errors[].name` ↔ contract API).
 */

export interface DqxContractRuleMeta {
  rule_type: string;
  check_function?: string;
  affected_columns?: string[];
  allowed_values?: string[];
}

export function normalizeDqxRuleName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function affectedColumnsFromRuleRow(r: Record<string, unknown>): string[] | undefined {
  const top = r.affected_columns;
  if (Array.isArray(top)) {
    const out = top.map((x) => String(x).trim()).filter(Boolean);
    return out.length ? out : undefined;
  }
  const check = r.check;
  if (!check || typeof check !== "object" || Array.isArray(check)) return undefined;
  const args = (check as { arguments?: unknown }).arguments;
  if (!args || typeof args !== "object" || Array.isArray(args)) return undefined;
  const a = args as Record<string, unknown>;
  if (Array.isArray(a.columns) && a.columns.length > 0) {
    return a.columns.map((x) => String(x).trim()).filter(Boolean);
  }
  if (a.column != null && String(a.column).trim()) {
    return [String(a.column).trim()];
  }
  return undefined;
}

function allowedValuesFromRuleRow(r: Record<string, unknown>): string[] | undefined {
  const check = r.check;
  if (!check || typeof check !== "object" || Array.isArray(check)) return undefined;
  const args = (check as { arguments?: unknown }).arguments;
  if (!args || typeof args !== "object" || Array.isArray(args)) return undefined;
  const allowed = (args as Record<string, unknown>).allowed;
  if (!Array.isArray(allowed)) return undefined;
  const uniqueValues = new Set<string>();
  const out: string[] = [];
  for (const raw of allowed) {
    const value = String(raw).trim();
    if (!value || uniqueValues.has(value)) continue;
    uniqueValues.add(value);
    out.push(value);
  }
  return out.length ? out : undefined;
}

/**
 * One map keyed by `normalizeDqxRuleName(rule.name)` for summary buckets, cell filters, and violation scope.
 */
export function buildContractRuleLookup(
  contractRules: Record<string, unknown>[]
): Map<string, DqxContractRuleMeta> {
  const m = new Map<string, DqxContractRuleMeta>();
  for (const r of contractRules) {
    const name = typeof r.name === "string" ? r.name.trim() : "";
    if (!name) continue;
    const key = normalizeDqxRuleName(name);
    const rule_type = typeof r.rule_type === "string" ? r.rule_type.trim() : "";
    const check_function =
      typeof r.check_function === "string" ? r.check_function.trim() : undefined;
    const affected_columns = affectedColumnsFromRuleRow(r);
    const allowed_values = allowedValuesFromRuleRow(r);
    m.set(key, {
      rule_type,
      ...(check_function ? { check_function } : {}),
      ...(affected_columns?.length ? { affected_columns } : {}),
      ...(allowed_values?.length ? { allowed_values } : {}),
    });
  }
  return m;
}

/**
 * Column-level lookup for constrained dropdown editing
 * Keyed by target column name, value is the sorted unique allowedValues list from the contract.
 */
export function buildColumnAllowedValuesLookup(
  contractRules: Record<string, unknown>[]
): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const r of contractRules) {
    const affected_columns = affectedColumnsFromRuleRow(r);
    const allowed_values = allowedValuesFromRuleRow(r);

    if (!affected_columns?.length || !allowed_values?.length) continue;

    const sorted = [...allowed_values].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    for (const col of affected_columns) {
      m.set(col, sorted);
    }
  }
  return m;
}

export function lookupContractRuleMeta(
  lookup: Map<string, DqxContractRuleMeta> | null | undefined,
  ruleName: string
): DqxContractRuleMeta | undefined {
  if (!lookup || !ruleName.trim()) return undefined;
  return lookup.get(normalizeDqxRuleName(ruleName));
}
